export const Addon = (json: IDietAddon): IDietAddonModel => {
	const { name, status, active_to_order_from, active_to_order_to, addon_category, media, ...rest } = json

	return {
		...rest,
		name: LangContentToModel(name),
		category: addon_category,
		status,
		activeToOrderFrom: active_to_order_from ? new Date(active_to_order_from) : null,
		activeToOrderTo: active_to_order_to ? new Date(active_to_order_to) : null,
		media: media ? media.map(m => Media(m)) : [],
	}
}

export const AddonMeal = (json: IDietAddonMeal): IDietAddonMealModel => DietMeal(json)

export const AddonDetails = (json: IDietAddonDetails): IDietAddonDetailsModel => {
	const {
		name,
		short_name,
		status,
		addon_category,
		min_order_qty,
		max_order_qty,
		active_to_order_from,
		active_to_order_to,
		description,
		recipe,
		filter_tags,
		marketing_tags,
		allergens,
		media,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		shortName: LangContentToModel(short_name),
		status,
		category: addon_category,
		activeToOrderFrom: active_to_order_from ? new Date(active_to_order_from) : null,
		activeToOrderTo: active_to_order_to ? new Date(active_to_order_to) : null,
		minOrderQty: min_order_qty,
		maxOrderQty: max_order_qty,
		description: LangContentToModel(description),
		recipe,
		media: media ? media.map(m => Media(m)) : [],
		filterTags: filter_tags ? filter_tags.map(tag => DietFilterTag(tag)) : [],
		marketingTags: marketing_tags ? marketing_tags.map(tag => DietMarketingTag(tag)) : [],
		allergens: allergens ? allergens.map(tag => DietAllergen(tag)) : [],
	}
}

export const addonDetailsToJSON = (model: IDietAddonDetailsModel): IDietAddonUpdatePayload => {
	const {
		name,
		shortName,
		status,
		category,
		activeToOrderFrom,
		activeToOrderTo,
		minOrderQty,
		maxOrderQty,
		description,
		recipe,
		filterTags,
		marketingTags,
		allergens,
		id,
		prices,
		ingredients,
		media,
	} = model

	return {
		name: LangContentToJson(name),
		short_name: LangContentToJson(shortName),
		addon_category: category,
		active_to_order_from: activeToOrderFrom ? activeToOrderFrom : undefined,
		active_to_order_to: activeToOrderTo ? activeToOrderTo : undefined,
		min_order_qty: minOrderQty === 0 ? null : minOrderQty,
		max_order_qty: maxOrderQty === 0 ? null : maxOrderQty,
		description: LangContentToJson(description),
		recipe: recipe ?? undefined,
		ingredients: ingredients ?? undefined,
		media: media ? media.map(m => mediaToJSON(m)) : [],
		filter_tags: filterTags.map(tag => tag.id),
		marketing_tags: marketingTags.map(tag => tag.id),
		allergens: allergens.map(allergen => allergen.id),
		id,
		status,
		prices,
	}
}

/**
 * Payloads
 */
export const DietAddonSharedToPayload = (
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonPayloadOthers,
): IDietAddonSharedPayload => {
	const { minOrderQty, maxOrderQty, category, isDateRange, activeToOrderFrom, activeToOrderTo, ingredients, recipe } =
		event.values

	const { filterTags, marketingTags, allergens } = others

	if (activeToOrderFrom) activeToOrderFrom.setHours(23, 59, 59, 999)
	if (activeToOrderTo) activeToOrderTo.setHours(23, 59, 59, 999)

	return {
		min_order_qty: minOrderQty === 0 ? null : minOrderQty,
		max_order_qty: maxOrderQty ?? null,
		filter_tags: filterTags,
		marketing_tags: marketingTags,
		price: undefined,
		recipe: recipe ?? undefined,
		active_to_order_from: isDateRange ? activeToOrderFrom : null,
		active_to_order_to: isDateRange ? activeToOrderTo : null,
		addon_category: category,
		ingredients: ingredients ?? undefined,
		status: EDietStatus.ACTIVE,
		allergens,
	}
}

export const DietAddonUpdateToPayload = (
	updated: IDietAddonDetailsModel,
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonPayloadOthers,
): IDietAddonUpdatePayload => {
	const { name, shortName, description } = event.values

	let _description
	if (updated.description) _description = LangContentToJson(updated.description, description)
	else if (description) _description = generateLangContentArray(description)
	else _description = null

	const shared = DietAddonSharedToPayload(event, others)

	return {
		...shared,
		id: updated.id,
		name: LangContentToJson(updated.name, name),
		short_name: LangContentToJson(updated.shortName, shortName),
		description: _description,
	}
}

export const DietAddonCreateToPayload = (
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonPayloadOthers,
): IDietAddonCreatePayload => {
	const { name, shortName, description } = event.values

	const shared = DietAddonSharedToPayload(event, others)

	return {
		...shared,
		name: generateLangContentArray(name),
		short_name: generateLangContentArray(shortName),
		description: generateLangContentArray(description),
	}
}

/**
 * Payload Meals
 */
export const DietAddonMealSharedToPayload = (
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonMealPayloadOthers,
) => {
	const { minOrderQty, maxOrderQty, category, isDateRange, activeToOrderFrom, activeToOrderTo } = event.values

	const { filterTags, marketingTags, allergens } = others

	return {
		min_order_qty: minOrderQty === 0 ? null : minOrderQty,
		max_order_qty: maxOrderQty,
		filter_tags: filterTags,
		marketing_tags: marketingTags,
		price: undefined,
		recipe: undefined,
		active_to_order_from: isDateRange ? activeToOrderFrom : undefined,
		active_to_order_to: isDateRange ? activeToOrderTo : undefined,
		addon_category: category,
		allergens,
	}
}

export const DietAddonMealCreateToPayload = (
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonMealPayloadOthers,
) => {
	const shared = DietAddonMealSharedToPayload(event, others)

	return {
		...shared,
	}
}

export const DietAddonMealUpdateToPayload = (
	updated: any,
	event: IFormSubmitEvent<IDietAddonDialogForm>,
	others: IDietAddonMealPayloadOthers,
) => {
	const shared = DietAddonMealSharedToPayload(event, others)

	return {
		...shared,
	}
}
